import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TitleAndFilters from './TitleAndFilters';
import {
  Container,
  AdvContainer,
  Section,
  ContentWrapper,
  DeckStage,
  DeckTrack,
  Page
} from './styled';
import { useOnScreen } from '../../../hooks/useOnScreen';
import { useHoverSide } from '../../../hooks/useHoverSide';
import { useScrollButtons } from '../../../hooks/useScrollButtons';
import type { AdvantagesBlockProps } from '../types';
import { useSwipeDeck } from '../../../hooks/useSwipeDeck';
import { PlacementPage } from './PlacementPage';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';

export const AdvantagesBlock: React.FC<AdvantagesBlockProps> = ({
  placements,
  setadvantagesRef,
  setVisibleTabRef
}) => {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isVisible = useOnScreen(wrapperRef);
  const { isSmall } = useScreenSize();
  const isClient = useClient();

  const {
    hoveredSide,
    handleSlideMouseMove,
    handleLeftArrowHover,
    handleRightArrowHover,
    handleArrowLeave
  } = useHoverSide();

  const { navBarWrapperRef, showLeft, showRight, scrollNav } = useScrollButtons(
    placements.length
  );

  const {
    stageRef,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    withTransition,
    txPx,
    slidePx,
    slots
  } = useSwipeDeck({
    count: placements.length,
    activeIdx,
    setActiveIdx,
    isEnabled: isSmall
  });

  useEffect(() => {
    setadvantagesRef?.(wrapperRef);
    if (isVisible) return setVisibleTabRef?.(wrapperRef);
  }, [isVisible]);

  if (!placements || placements.length === 0) return null;

  const isMobileClient = isClient && isSmall;

  return (
    <Container ref={wrapperRef}>
      <Section>
        <AdvContainer>
          <TitleAndFilters
            title={t('pasha.top_bar_advantage')}
            placements={placements}
            activeIdx={activeIdx}
            showLeft={showLeft}
            showRight={showRight}
            scrollNav={scrollNav}
            navBarWrapperRef={navBarWrapperRef}
            isSmall={isMobileClient}
            onPick={idx => {
              setActiveIdx(idx);
            }}
          />
        </AdvContainer>

        <ContentWrapper>
          <DeckStage
            ref={stageRef}
            onTouchStart={isMobileClient ? onTouchStart : undefined}
            onTouchMove={isMobileClient ? onTouchMove : undefined}
            onTouchEnd={isMobileClient ? onTouchEnd : undefined}
          >
            <DeckTrack
              $withTransition={withTransition}
              $usePx={isMobileClient}
              $txPx={isMobileClient ? txPx : 0}
            >
              <Page $slidePx={isMobileClient ? slidePx : undefined}>
                {slots.leftIdx != null ? (
                  <PlacementPage
                    placement={placements[slots.leftIdx]}
                    isVisible={isVisible}
                    hoveredSide={hoveredSide}
                    handleSlideMouseMove={handleSlideMouseMove}
                    handleArrowLeave={handleArrowLeave}
                    handleLeftArrowHover={handleLeftArrowHover}
                    handleRightArrowHover={handleRightArrowHover}
                    t={t}
                  />
                ) : (
                  <div aria-hidden />
                )}
              </Page>

              <Page $slidePx={isMobileClient ? slidePx : undefined}>
                <PlacementPage
                  placement={placements[slots.currentIdx]}
                  isVisible={isVisible}
                  hoveredSide={hoveredSide}
                  handleSlideMouseMove={handleSlideMouseMove}
                  handleArrowLeave={handleArrowLeave}
                  handleLeftArrowHover={handleLeftArrowHover}
                  handleRightArrowHover={handleRightArrowHover}
                  t={t}
                />
              </Page>

              <Page $slidePx={isMobileClient ? slidePx : undefined}>
                {slots.rightIdx != null ? (
                  <PlacementPage
                    placement={placements[slots.rightIdx]}
                    isVisible={isVisible}
                    hoveredSide={hoveredSide}
                    handleSlideMouseMove={handleSlideMouseMove}
                    handleArrowLeave={handleArrowLeave}
                    handleLeftArrowHover={handleLeftArrowHover}
                    handleRightArrowHover={handleRightArrowHover}
                    t={t}
                  />
                ) : (
                  <div aria-hidden />
                )}
              </Page>
            </DeckTrack>
          </DeckStage>
        </ContentWrapper>
      </Section>
    </Container>
  );
};
